import React, { useState, useEffect } from 'react';
import SEOMetaTags from '../components/SEOMetaTags';
import Header from '../components/Header';
import { trackPageView } from '../analytics';

const MergePdf = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Maximum number of files allowed
  const MAX_FILES = 2;
  
  // Track page view
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackPageView(window.location.pathname);
    }
  }, []);

  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFilesArray = Array.from(e.target.files);
      
      // Validate that all selected files are PDFs
      const allPdfs = newFilesArray.every(file => file.type === 'application/pdf');
      
      if (!allPdfs) {
        setError('Please select only PDF files');
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      // Check if total files would exceed the maximum
      if (newFilesArray.length > MAX_FILES) {
        setError(`You can upload up to ${MAX_FILES} PDF files only`);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      // Check if adding these files to existing selection would exceed the limit
      if (selectedFiles.length + newFilesArray.length > MAX_FILES) {
        setError(`You can upload up to ${MAX_FILES} PDF files in total. You already have ${selectedFiles.length} files selected.`);
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      // Add new files to existing selection
      setSelectedFiles([...selectedFiles, ...newFilesArray]);
      setError(null);
      setSuccess(false);
    }
  };
  
  // Handle file removal
  const removeFile = (indexToRemove) => {
    setSelectedFiles(selectedFiles.filter((_, index) => index !== indexToRemove));
    setSuccess(false);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      setError('Please select at least one PDF file');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const formData = new FormData();
      
      // Append all files to the FormData object with the key 'files'
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      // Send the POST request to the API endpoint
      const response = await fetch('https://api.temp2doc.com/utilities/api/merge-pdf', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      // Handle successful response
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // Create a download link and trigger it
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'merged-document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setSuccess(true);
    } catch (err) {
      setError(`Failed to merge PDFs: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <SEOMetaTags 
        title="Merge PDF Files Online - Combine PDFs into One File | Temp2Doc"
        description="Combine multiple PDF files into a single document quickly and easily with our free online PDF merger tool. No installation required."
        keywords="merge PDF, combine PDF, join PDF files, PDF combiner, PDF merger, free PDF tools"
        canonicalUrl="https://temp2doc.com/merge-pdf"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Merge PDF",
          "applicationCategory": "WebApplication",
          "operatingSystem": "Web browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": "Combine multiple PDF files into a single document quickly and easily with our free online PDF merger tool."
        }}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
          <div className="inline-block p-3 bg-primary-100 rounded-full mb-4">
            <svg className="w-12 h-12 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Merge PDF Files</h1>
          <p className="text-xl text-gray-600 mb-8">
            Combine multiple PDF files into a single document quickly and easily.
          </p>
        </section>

        {/* Upload Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select PDF Files (Max 2)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-primary-500 transition-colors">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="pdf-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none">
                      <span>Upload files</span>
                      <input 
                        id="pdf-upload"
                        type='file' 
                        className="sr-only" 
                        accept=".pdf" 
                        multiple 
                        onChange={handleFileChange}
                        aria-label="Upload PDF files"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF files only (max 2 files)</p>
                </div>
              </div>
              {error && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              {success && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-600">PDFs merged successfully!</p>
                </div>
              )}
            </div>

            {/* Selected Files */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Selected Files</h3>
              <div className="border border-gray-200 rounded-lg min-h-[150px] p-4 bg-gray-50">
          {selectedFiles.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No files selected yet</p>
          ) : (
            <ul className="flex flex-col space-y-2" aria-label="Selected PDF files">
              {selectedFiles.map((file, index) => (
                <li key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-xs">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => removeFile(index)} 
                    className="text-gray-500 hover:text-red-500 transition-colors"
                    title="Remove file"
                    aria-label={`Remove ${file.name}`}
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          )}
              </div>
            </div>

            <button 
              onClick={handleSubmit}
              disabled={isLoading || selectedFiles.length === 0} 
              className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Merging PDFs...' : 'Merge PDFs'}
            </button>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                1
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Files</h3>
              <p className="text-gray-600 text-sm">Select multiple PDF files from your device to merge.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                2
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Arrange Order</h3>
              <p className="text-gray-600 text-sm">Reorder the files to set the sequence of the final document.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-center w-12 h-12 bg-primary-100 text-primary-600 rounded-full mb-4 font-bold text-lg">
                3
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Download Merged PDF</h3>
              <p className="text-gray-600 text-sm">Click merge to combine all files into a single PDF document.</p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Merging</h3>
              <p className="text-gray-600 text-sm">
                Combine multiple PDF files into one document quickly and efficiently.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast Processing</h3>
              <p className="text-gray-600 text-sm">
                Merge your PDF files in seconds with our optimized processing engine.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Private</h3>
              <p className="text-gray-600 text-sm">
                Your files are processed securely and automatically deleted after merging.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200" aria-label="Related Tools">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <li>
                <a href="/chat-with-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Chat PDF With AI</span>
                </a>
              </li>
              <li>
                <a href="/split-remove-rotate-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Split Remove Rotate PDF</span>
                </a>
              </li>
              <li>
                <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">PDF to Word</span>
                </a>
              </li>
              <li>
                <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">PDF to Excel</span>
                </a>
              </li>
              <li>
                <a href="/pdf-to-image" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">PDF to Image</span>
                </a>
              </li>
              <li>
                <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
                </a>
              </li>
              <li>
                <a href="/protect-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Protect PDF</span>
                </a>
              </li>
              <li>
                <a href="/unlock-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Unlock PDF</span>
                </a>
              </li>
              <li>
                <a href="/transform-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Repair & Transform PDF</span>
                </a>
              </li>
              <li>
                <a href="/compress-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Compress PDF</span>
                </a>
              </li>
              <li>
                <a href="/diagnose-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Verify & Diagnose PDF</span>
                </a>
              </li>
              <li>
                <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
                  <span className="text-primary-600 font-medium">Back to Home</span>
                </a>
              </li>
            </ul>
          </nav>
        </section>
      </main>
    </>
  );
};

export default MergePdf;
